import { Button, IconButton, PlusIcon, Switch, TextField, XIcon } from "@rhema/ui";
import { type FormEvent, useState } from "react";

export const MAX_OPTIONS = 12;

export type NewPoll = { question: string; options: string[]; multiple: boolean };

// The form for a new poll: a question, 2 to 12 options and whether people may choose several.
export function PollComposer({ onSend }: { onSend: (poll: NewPoll) => void }) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [multiple, setMultiple] = useState(false);
  const filled = options.map((o) => o.trim()).filter(Boolean);
  const ready = question.trim().length > 0 && filled.length >= 2;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) onSend({ question: question.trim(), options: filled, multiple });
  };

  return (
    <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
      <TextField
        label="Question"
        placeholder="Ask something"
        maxLength={200}
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        autoFocus
      />
      <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
        <legend className="mb-1.5 p-0 text-[13px] font-semibold text-ink">Options</legend>
        {options.map((value, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: options are edited in place
          <div key={i} className="flex items-center gap-2">
            <TextField
              label={`Option ${i + 1}`}
              hideLabel
              placeholder={`Option ${i + 1}`}
              maxLength={100}
              value={value}
              onChange={(e) => setOptions((list) => list.map((o, j) => (j === i ? e.target.value : o)))}
              className="flex-1"
            />
            {options.length > 2 && (
              <IconButton
                label={`Remove option ${i + 1}`}
                variant="ghost"
                size={36}
                onClick={() => setOptions((list) => list.filter((_, j) => j !== i))}
              >
                <XIcon size={15} strokeWidth={2.4} />
              </IconButton>
            )}
          </div>
        ))}
        {options.length < MAX_OPTIONS && (
          <Button
            variant="ghost"
            size="sm"
            className="self-start"
            icon={<PlusIcon size={15} strokeWidth={2.6} />}
            onClick={() => setOptions((list) => [...list, ""])}
          >
            Add an option
          </Button>
        )}
      </fieldset>
      <div className="flex items-center gap-3 rounded-field bg-surface px-4 py-3">
        <span className="flex-1 text-[15px]">Let people choose more than one</span>
        <Switch label="Let people choose more than one" checked={multiple} onChange={setMultiple} />
      </div>
      <Button type="submit" size="lg" block disabled={!ready}>
        Send poll
      </Button>
    </form>
  );
}
